'use client';
import React, { useEffect, useRef, useState } from 'react';

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 20h20"/><path d="M5 20V8l7-6 7 6v12"/><path d="M9 20v-6h6v6"/>
        </svg>
      ),
      title: 'End-to-End Solutions',
      desc: 'From concept and design to manufacture, supply, installation, and maintenance.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M8.46 8.46a5 5 0 0 0 0 7.07"/>
        </svg>
      ),
      title: 'Custom Engineering',
      desc: 'Every system is engineered to your exact site dimensions and vehicle count.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: 'ISO Certified Quality',
      desc: 'All components manufactured to international quality standards at our Daman facility.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 10h2l2 3 2-6 2 3h2"/>
        </svg>
      ),
      title: 'Smart Automation',
      desc: 'AI-driven systems with remote monitoring and 24/7 technical support.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="M4.93 4.93l1.41 1.41"/><path d="M17.66 17.66l1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="M4.93 19.07l1.41-1.41"/><path d="M17.66 6.34l1.41-1.41"/>
        </svg>
      ),
      title: 'In-House Manufacturing',
      desc: 'Complete control over quality with our own manufacturing unit in Daman, India.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
        </svg>
      ),
      title: 'AMC & Support',
      desc: 'Annual Maintenance Contracts with rapid on-site response across India.'
    },
  ];

  return (
    <section
      id="about"
      className="py-20 bg-white"
      style={{ fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}>
      
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[#00AAE2] text-[15px] font-medium tracking-[2px] uppercase mb-3">Who We Are</p>
          <h2
            className="text-[#062543] font-medium mb-4"
            style={{ fontSize: '42px', lineHeight: '52px', fontFamily: 'Ubuntu, sans-serif' }}>
            About Bhargav Car Parking
          </h2>
          <p
            className="text-[#3A3A3A] max-w-[600px] mx-auto"
            style={{ fontSize: '17px', lineHeight: '28px', fontFamily: 'Ubuntu, sans-serif' }}>
            A young, modern and futuristic automated car parking systems provider based in Daman, India.
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left: Image */}
          <div
            className={`w-full lg:w-[45%] flex-shrink-0 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            
            <div className="relative overflow-hidden rounded-[12px]" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_176987612-1786006461972.png"
                alt="Bhargav Engineering manufacturing facility — in-house production of automated parking system components"
                className="w-full h-full object-cover" />
              
              {/* Overlay badge */}
              <div
                className="absolute bottom-5 left-5 px-5 py-3 rounded-[8px] text-white shadow-xl"
                style={{ background: 'rgba(6,37,67,0.92)', backdropFilter: 'blur(8px)' }}>
                <div className="text-[28px] font-bold text-[#00AAE2]" style={{ lineHeight: '1' }}>10+</div>
                <div className="text-[12px] text-white/70 mt-0.5">Years of Excellence</div>
              </div>
            </div>
          </div>

          {/* Right: Text + Features */}
          <div
            className={`w-full lg:w-[55%] transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            
            <p
              className="text-[#3A3A3A] mb-6"
              style={{ fontSize: '17px', lineHeight: '28px', fontFamily: 'Ubuntu, sans-serif' }}>
              Bhargav Car Parking is a young, modern and futuristic automated car parking systems provider based in Daman, India. We handle everything from concept and design to manufacture, supply, installation, and maintenance.
            </p>
            <p
              className="text-[#3A3A3A] mb-10"
              style={{ fontSize: '16px', lineHeight: '26px', fontFamily: 'Ubuntu, sans-serif' }}>
              With over a decade of experience, our team delivers precision-engineered parking solutions for residential, commercial, and industrial projects across India and abroad.
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features?.map((feature, i) =>
              <div
                key={i}
                className={`flex items-start gap-3 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${200 + i * 80}ms` }}>
                
                  <div
                    className="w-10 h-10 rounded-[8px] flex items-center justify-center flex-shrink-0"
                    style={{ background: '#EAF7FD' }}>
                    {feature?.icon}
                  </div>
                  <div>
                    <h3
                      className="text-[#062543] font-semibold mb-1"
                      style={{ fontSize: '15px', fontFamily: 'Ubuntu, sans-serif' }}>
                      {feature?.title}
                    </h3>
                    <p
                      className="text-[#6B7280]"
                      style={{ fontSize: '13px', lineHeight: '20px', fontFamily: 'Ubuntu, sans-serif' }}>
                      {feature?.desc}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);
}